import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { BookOpen, Calendar, ArrowRight, Loader2, Target, AlertTriangle, Newspaper, BookMarked } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PreviousYears() {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingYear, setStartingYear] = useState(null);
  const [caToggles, setCaToggles] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/pyq/years`).then(r => {
      setPapers(r.data);
      const toggles = {};
      r.data.forEach(p => { toggles[p.year] = true; });
      setCaToggles(toggles);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const toggleCa = (year) => {
    setCaToggles(prev => ({ ...prev, [year]: !prev[year] }));
  };

  const startPaper = async (year) => {
    setStartingYear(year);
    try {
      const res = await axios.post(`${API}/pyq/start`, { year, include_ca: caToggles[year] ?? true });
      toast.success(`${year} paper loaded! ${res.data.question_count} questions.`);
      navigate(`/test/${res.data.test_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load paper.");
    }
    setStartingYear(null);
  };

  const yearColors = {
    2024: "from-indigo-500/10 to-indigo-500/5 border-indigo-500/30",
    2023: "from-emerald-500/10 to-emerald-500/5 border-emerald-500/30",
    2022: "from-amber-500/10 to-amber-500/5 border-amber-500/30",
    2021: "from-pink-500/10 to-pink-500/5 border-pink-500/30",
    2020: "from-purple-500/10 to-purple-500/5 border-purple-500/30",
    2019: "from-teal-500/10 to-teal-500/5 border-teal-500/30",
  };

  return (
    <div className="p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="pyq-page">
      <div>
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
          Previous Year Papers
        </h1>
        <p className="text-muted-foreground mt-2 text-base">
          Practice with actual UPSC Prelims patterns from 2019-2024
        </p>
      </div>

      {/* Info Card */}
      <Card className="bg-card border-border border-l-4 border-l-indigo-500">
        <CardContent className="p-5 space-y-2">
          <p className="text-sm text-muted-foreground">
            These questions are <strong className="text-foreground">isolated from your main question bank</strong> and do not affect your mock test analytics.
          </p>
          <div className="flex items-start gap-2 p-3 rounded-sm bg-amber-500/5 border border-amber-500/20">
            <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-300/80">
              <strong>Current Affairs Toggle:</strong> Questions based on events specific to that year are marked with an orange tag.
              You can <strong>exclude</strong> them before starting — useful since those events are now outdated.
              Static/conceptual questions remain valuable regardless of year.
            </p>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-64 bg-secondary rounded animate-pulse" />)}
        </div>
      ) : papers.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <BookOpen size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No previous year papers available yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {papers.map((paper) => {
            const includeCa = caToggles[paper.year] ?? true;
            const effectiveCount = includeCa ? paper.question_count : paper.static_question_count;
            return (
              <Card
                key={paper.year}
                data-testid={`pyq-year-${paper.year}`}
                className={`bg-gradient-to-br ${yearColors[paper.year] || "border-border"} border transition-all hover:-translate-y-1 duration-300`}
              >
                <CardContent className="p-6 space-y-4">
                  {/* Year & Count */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-muted-foreground" />
                      <span className="font-mono text-4xl font-bold">{paper.year}</span>
                    </div>
                    <Badge variant="outline" className="text-xs font-mono">
                      {effectiveCount} Q
                    </Badge>
                  </div>

                  {/* Key Themes */}
                  <div className="flex flex-wrap gap-1.5">
                    {(paper.key_themes || []).slice(0, 4).map((theme, i) => (
                      <Badge key={i} variant="outline" className="text-xs py-0.5 font-normal">
                        {theme}
                      </Badge>
                    ))}
                  </div>

                  {/* CA Toggle */}
                  <div className="p-3 rounded-sm bg-secondary/50 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Newspaper size={14} className="text-orange-400" />
                        <span className="text-xs font-medium">Include Current Affairs</span>
                      </div>
                      <Switch
                        data-testid={`ca-toggle-${paper.year}`}
                        checked={includeCa}
                        onCheckedChange={() => toggleCa(paper.year)}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <BookMarked size={12} />
                        Static: {paper.static_question_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <Newspaper size={12} className="text-orange-400" />
                        CA: {paper.ca_question_count}
                      </span>
                    </div>
                    {!includeCa && (
                      <p className="text-xs text-amber-400/80 italic">
                        {paper.ca_question_count} year-specific CA questions will be skipped
                      </p>
                    )}
                  </div>

                  {/* Attempt Status */}
                  {paper.attempted && (
                    <div className="p-2 rounded-sm bg-primary/5 border border-primary/20 flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Last Score:</span>
                      <span className="font-mono font-bold text-primary">{paper.last_score?.toFixed(1)}%</span>
                    </div>
                  )}

                  {/* Start Button */}
                  <Button
                    data-testid={`start-pyq-${paper.year}`}
                    className="w-full bg-primary hover:bg-primary/90 gap-2"
                    onClick={() => startPaper(paper.year)}
                    disabled={startingYear === paper.year || effectiveCount === 0}
                  >
                    {startingYear === paper.year ? (
                      <><Loader2 size={16} className="animate-spin" /> Loading...</>
                    ) : paper.attempted ? (
                      <><Target size={16} /> Reattempt ({effectiveCount} Q)</>
                    ) : (
                      <><ArrowRight size={16} /> Start Paper ({effectiveCount} Q)</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
